import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useParams, useLocation } from 'react-router-dom';
import { Menu, X, Eye } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/layout/LanguageSelector';
import { isGuestPreview } from '../services/dataService';

const NAV = [
  { to: '', label: 'Home', end: true },
  { to: 'eventi', label: 'Eventi' },
  { to: 'vicino-a-te', label: 'Vicino a te' },
  { to: 'arte-musei', label: 'Arte & Musei' },
  { to: 'servizi', label: 'Servizi', feature: 'services' },
  { to: 'esperienze', label: 'Esperienze', feature: 'experiences' },
  { to: 'info-bnb', label: 'Info BnB' },
];

export function GuestLayout() {
  const { token } = useParams();
  const { lang, setLang, languages, config } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const preview = isGuestPreview(token);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const brand = config?.tenant?.brand?.name || 'Guest Portal';
  const features = config?.features || [];
  const nav = NAV.filter((n) => !n.feature || features.includes(n.feature));
  const linkCls = ({ isActive }) =>
    `text-sm transition-colors ${isActive ? 'text-paelbi-brown font-semibold' : 'text-paelbi-ink/75 hover:text-paelbi-brown'}`;

  return (
    <div className="flex min-h-screen flex-col bg-paelbi-bg">
      {preview && (
        <div className="flex items-center justify-center gap-2 bg-paelbi-ink px-4 py-2 text-center text-xs font-semibold text-white" data-testid="guest-preview-banner">
          <Eye size={14} /> Anteprima privata — così la vedranno i tuoi ospiti (non pubblicata pubblicamente)
        </div>
      )}
      <header className="sticky top-0 z-50 border-b border-paelbi-line/70 bg-paelbi-bg/90 backdrop-blur-md">
        <div className="mx-auto flex h-[68px] max-w-container items-center justify-between px-6">
          <NavLink to={`/g/${token}`} end className="font-display text-xl text-paelbi-ink" data-testid="guest-brand">{brand}</NavLink>
          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 lg:flex">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to ? `/g/${token}/${n.to}` : `/g/${token}`} end={n.end} className={linkCls}>{n.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSelector className="hidden sm:block" value={lang.toUpperCase()}
              languages={(languages || ['it']).map((l) => l.toUpperCase())} onChange={(l) => setLang(l.toLowerCase())} />
            <button className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-btn text-paelbi-brown"
              aria-label={open ? 'Chiudi menu' : 'Apri menu'} onClick={() => setOpen((v) => !v)}>
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="border-t border-paelbi-line bg-paelbi-bg lg:hidden">
            <nav className="mx-auto flex max-w-container flex-col gap-3 px-6 py-4">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to ? `/g/${token}/${n.to}` : `/g/${token}`} end={n.end} className={linkCls}>{n.label}</NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>
      <main key={lang} className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-paelbi-line bg-white py-6 text-center text-xs text-paelbi-ink/50">
        {brand} · powered by Domira
      </footer>
    </div>
  );
}

export default GuestLayout;
